
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "next-themes";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import TrackingScripts from "@/components/tracking/TrackingScripts";
import SpeedInsights from "@/components/tracking/SpeedInsights";
import Analytics from "@/components/tracking/Analytics";
import CookieConsent from "@/components/tracking/CookieConsent";
import Index from "./pages/Index";
import Inventory from "./pages/Inventory";
import VehicleDetail from "./pages/VehicleDetail";
import LeadCapture from "./pages/LeadCapture";

import Contact from "./pages/Contact";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import SellYourCar from "./pages/SellYourCar";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/Dashboard";
import InventoryManagement from "./pages/admin/InventoryManagement";
import LeadsManagement from "./pages/admin/LeadsManagement";
import SellRequestsManagement from "./pages/admin/SellRequestsManagement";
import UsersManagement from "./pages/admin/UsersManagement";
import FAQManagement from "./pages/admin/FAQManagement";
import DealsManagement from "./pages/admin/DealsManagement";
import CustomersList from "./pages/admin/CustomersList";
import CustomerProfile from "./pages/admin/CustomerProfile";
import TasksManagement from "./pages/admin/TasksManagement";
import Security from "./pages/admin/Security";
import AdminVehicleDetail from "./pages/admin/VehicleDetail";
import BusinessExpenses from "./pages/admin/BusinessExpenses";
import FinancialDashboard from "./pages/admin/FinancialDashboard";
import CommunicationHub from "./pages/admin/CommunicationHub";
import RecycleBin from "./pages/admin/RecycleBin";
import AuditLog from "./pages/admin/AuditLog";
import NotFound from "./pages/NotFound";

// Global Styles & Fonts
import './index.css';
import '@fontsource/montserrat/600.css';
import '@fontsource/montserrat/700.css';
import '@fontsource/montserrat/800.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

import PreApproval from "./pages/PreApproval";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <TooltipProvider>
        <Sonner />
        <AuthProvider>
          <ScrollToTop />
          <TrackingScripts />
          <Analytics />
          <SpeedInsights />
          <CookieConsent />
          <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/inventory" element={<Inventory />} />
              <Route path="/vehicle/:id" element={<VehicleDetail />} />
              <Route path="/pre-approval" element={<PreApproval />} />
              <Route path="/sell-your-car" element={<SellYourCar />} />
              <Route path="/inquire" element={<LeadCapture />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              <Route path="/admin" element={<ProtectedRoute requireAdmin><ErrorBoundary><AdminLayout /></ErrorBoundary></ProtectedRoute>}>
                <Route index element={<AdminDashboard />} />
                <Route path="inventory" element={<InventoryManagement />} />
                <Route path="inventory/:id" element={<AdminVehicleDetail />} />
                <Route path="finances" element={<FinancialDashboard />} />
                <Route path="expenses" element={<BusinessExpenses />} />
                <Route path="deals" element={<DealsManagement />} />
                <Route path="customers" element={<CustomersList />} />
                <Route path="customers/:id" element={<CustomerProfile />} />
                <Route path="leads" element={<LeadsManagement />} />
                <Route path="sell-requests" element={<SellRequestsManagement />} />
                <Route path="tasks" element={<TasksManagement />} />
                <Route path="messages" element={<CommunicationHub />} />
                <Route path="users" element={<UsersManagement />} />
                <Route path="faqs" element={<FAQManagement />} />
                <Route path="recycle-bin" element={<RecycleBin />} />
                <Route path="audit-log" element={<AuditLog />} />
                <Route path="security" element={<Security />} />
              </Route>

              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
        </AuthProvider>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
